import * as React from "react";
import { Alert, AlertTitle, Typography } from "@mui/material";
import { GaugeIcon } from "@phosphor-icons/react/dist/ssr/Gauge";

/**
 * What the money surfaces say instead of a cost that does not exist.
 *
 * UX §12 note 2 and the §13 header both ask for cost accruing in public from the first minute:
 * "call time so far S$ 0.00" climbing, and "cost to date S$ 3,038" on the running programme.
 * Neither can be answered today, and the reason is specific rather than a shrug.
 *
 * There is no usage ledger in this module. Nothing writes a row per call carrying what that call
 * cost at the rate the quote froze; `campaign_request_quote_line` holds the *offer*, and the
 * campaign engine records `call.duration` in seconds and nothing about money at all. Turning
 * seconds into a figure a client is told they owe needs the parts nobody has built: which rate a
 * given second was billed at, the carrier leg beside our own, the rounding rule, and a record
 * that survives a rate change mid-campaign. Multiplying talk time by a price-book rate on the
 * screen would produce a number that looks exactly like a bill and matches no invoice — which is
 * the one failure a "cost to date" display exists to prevent.
 *
 * So the screens publish the money that genuinely exists — the committed set-up and the quoted
 * estimate — and say plainly that call time is not metered yet. The tiles are kept in place
 * rather than removed: an absent tile reads as "nothing has been spent", which is a stronger and
 * less true claim than this one.
 */

/** The value every unmetered money field shows. One string, so the screens cannot drift. */
export const NOT_METERED = "Not yet metered";

/** The tooltip behind that value, wherever there is only room for a tooltip. */
export const NOT_METERED_HELP =
	"We do not publish a running call-time figure yet. The setup charge beside it is committed and real.";

export interface MeteringGapProps {
	/** Client-facing copy avoids naming the ledger; admin copy names it. */
	variant?: "client" | "admin";
}

export function MeteringGap({ variant = "client" }: MeteringGapProps): React.JSX.Element {
	return (
		<Alert severity="info" icon={<GaugeIcon />}>
			<AlertTitle>Call time is not metered yet</AlertTitle>
			<Typography variant="body2">
				{variant === "admin" ? (
					<React.Fragment>
						Talk time is recorded in seconds, never against a rate, so the only real figures above are the committed
						setup and the quoted estimate.
					</React.Fragment>
				) : (
					<React.Fragment>
						Your setup charge is committed and shown above, and the estimate is what the quote assumed. We would rather
						show you nothing than a running figure that will not match your invoice.
					</React.Fragment>
				)}
			</Typography>
		</Alert>
	);
}
